import { Component, OnInit, OnDestroy, ViewChild, inject } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BookManagementService } from '../book-management.service';
import { AdminService } from '../../admin/admin.service';
import { BmLaunchModalComponent } from '../bm-launch-modal/bm-launch-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { RbGridComponent } from '../../shared-components/rb-grid/rb-grid.component';
import { SessionStorageService } from '../../services/session-storage.service';
import { saveAs } from '@progress/kendo-file-saver';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UtilityAppsService } from '../../shared-components/utility-apps/utility-apps.service';
import { bmLinks } from '../../lib/toolbar-links';
import { bmUtilityApps } from '../../lib/utility-apps';
import { GridResults } from '../../models/general-models';

import { PolicyDetailsComponent } from '../policy-details/policy-details.component';
import { HomeOfficeReportsComponent } from '../home-office-reports/home-office-reports.component';
import { RepReportsComponent } from '../rep-reports/rep-reports.component';
import { UtilityToolbarComponent } from '../../shared-components/utility-toolbar/utility-toolbar.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { BmWidgetFiltersComponent } from '../bm-widget-filters/bm-widget-filters.component';

@Component({
  selector: 'app-book-management',
  templateUrl: './book-management.component.html',
  styleUrls: ['./book-management.component.scss'],
  imports: [
    PolicyDetailsComponent,
    RbGridComponent,
    HomeOfficeReportsComponent,
    RepReportsComponent,
    UtilityToolbarComponent,
    MatProgressSpinnerModule,
    MatIconModule,
    CommonModule,
    MatTooltipModule,
    MatButtonModule,
  ],
})
export class BookManagementComponent implements OnInit, OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  adminService = inject(AdminService);
  bmSvc = inject(BookManagementService);
  private dialog = inject(MatDialog);
  private location = inject(Location);
  private route = inject(Router);
  private snacky = inject(MatSnackBar);
  private ss = inject(SessionStorageService);
  private uas = inject(UtilityAppsService);

  @ViewChild(RbGridComponent) rbGrid: RbGridComponent;

  unsubscribe: Subject<any> = new Subject();
  policyId = '';
  navCollapsed = false;
  crumbConfig: any[] = [];
  gridConfig = {
    title: 'Annuity Book Management',
    gridName: 'BookManagement',
    showGridFilters: true,
    showFilterManager: true,
    manualSearch: true,
    searchButtonText: 'Search Policies',
    showBulkActions: false,
    downloadGridButton: false,
    gridActionColumn: true,
    gridActions: [
      {
        icon: 'preview',
        color: 'primary',
        text: 'View Policy',
        action: this.viewPolicy.bind(this),
      },
    ],
    selectableRows: true,
    rowSelector: 'ID',
    gridType: 'book',
    detailViewRoute: '/book-management/',
    showBridgeIdSearch: true,
    showGridColumnFilters: true,
    reorderable: true,
  };
  current = 'pm';
  homeOfficeUser = false;
  hasRepCodes = false;
  launchButton;
  fileLoading = false;
  bmLinks = bmLinks;

  ngOnInit(): void {
    this.bmSvc.launchButton$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(buttonVals => {
        if (!buttonVals['ShowLaunchButton']) {
          this.gridConfig.selectableRows = false;
        }
        this.launchButton = buttonVals;
      });
    this.navCollapsed = this.ss.get('utilityToolbarCollapsed');
    this.current = this.location.path().split('/')[2] || 'pm';
    const globals = this.ss.get('globals');
    const repCodes = this.ss.get('repUnits');

    if (globals.user.managerUnitCount > 0) {
      this.homeOfficeUser = true;
    } else if (globals.user.managerUnitCount === 0) {
      this.homeOfficeUser = false;
    }

    if (repCodes && repCodes.length > 0) {
      this.hasRepCodes = true;
    }

    if (this.current === 'office-view') {
      this.crumbConfig.push({
        label: 'Office Reports',
        link: '/office-view',
      });
    } else if (this.current === 'rep-reports') {
      this.crumbConfig.push({
        label: 'Rep Reports',
        link: '/rep-reports',
      });
    } else if (this.current === 'pm') {
      this.crumbConfig.push({
        label: 'Book Management',
        link: '/book-management',
      });
    }

    this.activatedRoute.params
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(params => {
        if (params?.id) {
          this.policyId = params.id;
          this.crumbConfig.push({
            label: this.policyId,
            link: `book-management/${this.policyId}`,
          });
        } else {
          this.policyId = '';
        }
      });

    this.uas.appsList.set(bmUtilityApps);
  }

  ngOnDestroy() {
    this.uas.appsList.set([]);
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  nav(location) {
    this.route.navigate([`/${location}`]);
  }

  viewPolicy(ev, dataItem, context) {
    if (ev) {
      ev.preventDefault();
      ev.stopPropagation();
    }

    window.open(
      `${context.location.prepareExternalUrl('')}book-management/${
        dataItem.ID
      }`,
      '_blank'
    );
  }

  getGridSetup(): Observable<GridResults> {
    return this.bmSvc.gridSetup();
  }

  getGridData(reqData) {
    return this.bmSvc.policyManagementData(reqData);
  }

  downloadPolicyList() {
    this.fileLoading = true;
    this.snacky.open(`File downloading. Please wait.`, 'Close', {
      duration: 4000,
    });
    const queryData = this.rbGrid.generateQueryData(null, {
      take: 100000,
      skip: 0,
    });
    queryData['excludedColumns'] = [...this.rbGrid.hidden, 'CarrierCode'];

    this.bmSvc.downloadPolicyManagementData(queryData).subscribe({
      next: resp => {
        if (resp) {
          let fileData;
          const filename = `AnnuityBookManagement.csv`;
          const reader = new FileReader();
          reader.readAsDataURL(resp);

          reader.onloadend = () => {
            this.fileLoading = false;
            fileData = reader.result;
            saveAs(fileData, filename);
            this.snacky.open(`Download complete.`, 'Close', {
              duration: 4000,
            });
          };
        } else {
          this.fileLoading = false;
          this.snacky.open(
            `Oops! Something went wrong, please try again later.`,
            'Close',
            {
              duration: 4000,
            }
          );
        }
      },
      error: err => {
        console.warn('Error: ', err);
        this.fileLoading = false;
        this.snacky.open(
          `Oops! Something went wrong, please try again later.`,
          'Close',
          {
            duration: 4000,
          }
        );
      },
    });
  }

  launchToCase(launchApp) {
    this.dialog.open(BmLaunchModalComponent, {
      panelClass: 'app-bm-launch-modal',
      width: '98vw',
      autoFocus: false,
      data: {
        label: this.launchButton.LaunchButtonLabel,
        selectedAnnuities: this.rbGrid.selectedTradeList,
        launchLocation: launchApp,
      },
    });
  }

  toggleNavCollapse() {
    this.navCollapsed = !this.navCollapsed;
  }

  disableLaunchTo() {
    return this.rbGrid.selectedTradeList.length <= 0;
  }
}
